






































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';

import { xProcessWebservice } from '@/webservices/xprocess';
import { xSubProcessWebservice } from '@/webservices/xsubprocess';
import { tagWebservice } from '@/webservices/tag';

import type { XProcess } from '@/models/xprocess';
import type { XSubProcess } from '@/models/xsubprocess';
import type { XLesson } from '@/models/xlesson';
import type { Worksheet } from '@/models/worksheet';
import type { Project } from '@/models/project';
import type { User } from '@/models/user';
import type { CompanySetting } from '@/models/companySetting';
import type { Tag } from '@/models/tag';

import { ApprovalState } from '@/components/prine/enums/ApprovalState';
import SearchField from '@/components/prine/SearchField.vue';
import StoreUpdateXLessonModal from '@/components/knowledge/x/lessons/StoreUpdateXLessonModal.vue';
import FilteringSetting from '@/components/knowledge/x/lessons/FilteringSetting.vue';

import { FilterManager } from '@/helpers/filter/FilterManager';
import { FilterOptions } from '@/helpers/filter/models/FilterOptions';
import { FilterData } from '@/helpers/filter/models/FilterData';
import MoreInfoModal from '@/components/MoreInfoModal.vue';
import LessonsList from '@/components/knowledge/lessons/LessonsList.vue';
import { hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';

@Component({
    name: 'GroupedXLessonsList',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasPermission },
    components: {
        StoreUpdateXLessonModal,
        SearchField,
        FilteringSetting,
        MoreInfoModal,
        LessonsList,
    },
})
export default class GroupedXLessonsList extends Vue {
    private moreInfoShowModalActive: boolean = false;
    private moreInfoModalInsight: string;
    private moreInfoModalContent: string;
    private moreInfoModalId: number;

    private option: {
        filterByColumn: boolean;
        filterable: boolean;
    };
    private isStoreUpdateLessonModalActive = false;
    private showOnlyMyLessons = false;

    private filteredSubPhases = new Array<XSubProcess>();
    private originalSubPhases = new Array<XSubProcess>();

    private subPhases = new Array<XSubProcess>();
    private phases = new Array<XProcess>();

    private selectedPhase: XProcess = {} as XProcess;
    private selectedSubPhase: XSubProcess = {} as XSubProcess;
    private selectedLesson: XLesson = {} as XLesson;

    private allSubPhases: XSubProcess = {} as XSubProcess;

    private tags = new Array<Tag>();
    private selectedTag = {} as Tag;
    private allTags: Tag = {} as Tag;

    private worksheet: Worksheet = {} as Worksheet;

    private isSearching: boolean = false;
    private searchQuery: string = '';

    private isFilteringSettingActive = false;

    private filterManager: FilterManager = new FilterManager();
    private filterOptions: FilterOptions = {} as FilterOptions;
    private tableCollapsed: boolean = false;
    private openedDetailRows: number[] = [];

    @Getter('project/selectedProject') private selectedProject: Project;
    @Getter('account/userInfo') private userInfo: User;
    @Getter('account/companySetting') private companySetting: CompanySetting;
    @Getter('utils/currentLocale') private currentLocale: string;

    public toggleStoreUpdateLessonModal(pmLesson: XLesson = {} as XLesson) {
        this.selectedLesson = pmLesson;
        this.isStoreUpdateLessonModalActive = !this.isStoreUpdateLessonModalActive;
        this.loadSubPhasesWithLessons();
    }

    @Watch('searchQuery')
    private onSearchQueryValueChange() {
        this.search();
    }

    @Watch('showEmptySubphases')
    private onFiltersChanged() {
        this.filter();
    }

    private mounted() {
        this.loadTags();
        // The id is passed as string, parse it
        this.loadSelectedPhase();
    }

    get showEmptySubphases() {
        return this.$store.getters['knowledge/showEmptySubphases'];
    }

    set showEmptySubphases(value: boolean) {
        this.$store.commit('knowledge/setShowEmptySubphases', value);
    }

    get canResetFilter() {
        return (
            this.selectedTag !== this.allTags ||
            this.selectedSubPhase !== this.allSubPhases ||
            this.showEmptySubphases ||
            this.showOnlyMyLessons
        );
    }

    get canSeeLessons() {
        return hasPermission(PERMISSIONS.APPEX_X_LESSON);
    }

    private initializeMoreInfoModal(lesson: XLesson) {
        this.moreInfoModalContent = lesson.more_info;
        this.moreInfoModalInsight = lesson.insight;
        this.moreInfoModalId = lesson.id;
        this.moreInfoShowModalActive = true;
    }

    private loadTags() {
        tagWebservice
            .load()
            .then((response) => {
                this.tags = response.data;

                this.allTags.id = 0;
                this.allTags.title = this.$t('keywords.allTags').toString();

                this.selectedTag = this.allTags;

                // Add it in the front of the array
                this.tags.unshift(this.allTags);

                // getting selectedTagId from the query and select the matching tag
                const selectedTagId = Number(this.$route.query.selectedTagId);
                const tag = this.tags.find((el) => el.id === selectedTagId);

                if (tag) {
                    this.selectedTag = tag;
                    this.isFilteringSettingActive = true;
                }

                if (this.originalSubPhases && this.originalSubPhases.length > 0) {
                    this.filter();
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private updateSearchQuery(value: string) {
        this.searchQuery = value;
    }

    private search() {
        this.isSearching = true;
        this.filter();
        this.isSearching = false;
    }

    private goBack() {
        let filterByLessons = '';
        if (this.$route.query.filterByLessons) {
            filterByLessons = this.$route.query.filterByLessons as any;
        }
        this.$router.push({
            name: 'CombinedKnowledge',
            query: {
                searchQuery: encodeURIComponent(this.searchQuery),
                filterByLessons,
            },
        });
    }

    private loadSelectedPhase() {
        xProcessWebservice.getById(this.$route.query.phaseId.toString()).then((response) => {
            this.selectedPhase = response.data;
            this.loadSubPhasesWithLessons();
        });
    }

    private reload() {
        this.loadSubPhasesWithLessons();
    }

    private loadSubPhasesWithLessons() {
        xSubProcessWebservice.load(this.selectedPhase.id, ApprovalState.ALL).then((response) => {
            this.filteredSubPhases = response.data;
            this.originalSubPhases = response.data;

            this.subPhases = response.data.map((pmSubPhase) => {
                return {
                    id: pmSubPhase.id,
                    title: pmSubPhase.title,
                } as XSubProcess;
            });

            if (this.$route.query.searchQuery && this.$route.query.searchQuery !== '') {
                this.searchQuery = decodeURIComponent(this.$route.query.searchQuery.toString()) as any;
                this.search();
            }
            this.allSubPhases.id = 0;
            this.allSubPhases.title =
                this.$t('keywords.all').toString() + this.currentLocale === 'en'
                    ? this.companySetting.x_en_sub_process_title
                    : this.companySetting.x_de_sub_process_title;

            this.selectedSubPhase = this.allSubPhases;

            // Add it in the front of the array
            this.subPhases.unshift(this.allSubPhases);

            this.filter();
            this.init();
        });
    }

    private hr(index) {
        return index > 1;
    }

    private init() {
        this.openedDetailRows = this.filteredSubPhases.map((el) => el.id);
    }

    private clearSearch() {
        // Reset the search query
        this.searchQuery = '';
    }

    private getSubPhaseTitle(subPhase: XSubProcess) {
        const prefix = subPhase.order !== undefined ? subPhase.order + '. ' : '';
        return prefix + subPhase.title;
    }

    private subPhaseChanged(value: XSubProcess) {
        this.filter();
    }

    private tagChanged(value: Tag) {
        this.selectedTag = value;
        this.filter();
    }

    private showOnlyMyLessonsChanged(value: boolean) {
        this.showOnlyMyLessons = value;
        this.filter();
    }

    private toggleFilteringSettings() {
        this.isFilteringSettingActive = !this.isFilteringSettingActive;
    }

    private filter() {
        const data = JSON.parse(JSON.stringify(this.originalSubPhases)) as FilterData[];
        // console.log('filter data:', data);

        this.setFilterOptions();
        // console.log('calling FilterManager->filter');
        this.filteredSubPhases = (this.filterManager.filter(data, this.filterOptions) as unknown) as XSubProcess[];
    }

    private resetFilter() {
        this.selectedTag = this.allTags;
        this.selectedSubPhase = this.allSubPhases;
        this.showEmptySubphases = false;
        this.showOnlyMyLessons = false;

        this.filter();
    }

    private setFilterOptions() {
        this.filterOptions.userId = this.userInfo.id;
        this.filterOptions.searchQuery = this.searchQuery;
        this.filterOptions.tag = this.selectedTag;
        this.filterOptions.xSubProcess = this.selectedSubPhase;
        this.filterOptions.showEmptyElements = this.showEmptySubphases;
        this.filterOptions.onlyMyLessons = this.showOnlyMyLessons;
    }

    private toggleDetailRows() {
        if (this.tableCollapsed) {
            this.openedDetailRows = this.filteredSubPhases.map((el) => el.id);
        } else {
            this.openedDetailRows = [];
        }
        this.tableCollapsed = !this.tableCollapsed;
    }

    private getLessonsCount(lessons: XLesson[]) {
        const count = (cLessons: XLesson[]): number => {
            return cLessons.reduce((base, lesson) => {
                if (lesson.sub_lessons) {
                    base += count(lesson.sub_lessons);
                }

                return base + 1;
            }, 0);
        };

        return count(lessons);
    }
}
